// Tutorial Override System
// Allows specifying exact AI responses for specific board states in tutorials
// This gives precise control over AI behavior to create predictable tutorial experiences

/**
 * Normalize FEN strings to use consistent king symbols for comparison
 * Converts both ♛/♕ symbols to K/k format so overrides work with either
 * Also strips turn indicator prefixes like 'd' for dark turn
 * LLM never add a description to the FEN overrides, they are not helpful
 * @param fen The FEN string to normalize
 * @returns Normalized FEN string using K/k format
 */
function normalizeFEN(fen: string): string {
  return fen
    .replace(/^d/, '')     // Remove dark turn indicator prefix
    .replace(/♕/g, 'k')    // Light king crown → k (light player)
    .replace(/♛/g, 'K')    // Dark king crown → K (dark player)
    .replace(/✳️/g, '*')   // Sparkle emoji → asterisk (ability symbol)
    .replace(/⭐/g, '*')    // Star emoji → asterisk (ability symbol)
    .replace(/🌟/g, '*');   // Glowing star emoji → asterisk (ability symbol)
}

export interface TutorialOverride {
  /** The exact FEN state that triggers this override */
  triggerFEN: string;
  /** The target FEN state to set after the override */
  responseFEN: string;
  /** Optional delay in milliseconds before applying the override (default: 200ms) */
  delayMs?: number;
  /** Optional description for debugging purposes */
  description?: string;
  /** Optional function to modify pieces after FEN is applied */
  postProcess?: (gameState: any) => void;
}

export interface ConstellationOverrides {
  [tutorialIndex: number]: TutorialOverride[];
}

/**
 * Tutorial overrides organized by constellation and tutorial index
 * Each tutorial can have multiple overrides for different board states
 */
export const TUTORIAL_OVERRIDES: { [constellation: string]: ConstellationOverrides } = {
  'Bear': {
    1: [ // Bear tutorial 2 (index 1) - "How to race"
      {
        triggerFEN: '4BK3/8/6bk1/8/8',
        responseFEN: '4BK3/6bk1/8/8/8'
      },
      {
        triggerFEN: '8/4BK1bk1/8/8/8',
        responseFEN: '8/4BK3/6bk1/8/8'
      },
      {
        triggerFEN: '8/6bk1/8/5BK2/8',
        responseFEN: '8/8/7bk/5BK2/8'
      },
      {
        triggerFEN: '8/4BK3/6bk1/8/8',
        responseFEN: '8/4BK1bk1/8/8/8'
      },
      {
        triggerFEN: '8/6bk1/4BK3/8/8',
        responseFEN: '8/8/4BK1bk1/8/8'
      },
      {
        triggerFEN: '4BK3/6bk1/8/8/8',
        responseFEN: '4BK1bk1/8/8/8/8'
      },
      {
        triggerFEN: '8/8/6bk1/4BK3/8',
        responseFEN: '8/8/8/4BK1bk1/8'
      }
      // Add more overrides for Bear tutorial 2 here as needed
    ],
    2: [ // Bear tutorial 3 (index 2) - "Predator" - Multiple win conditions
      {
        triggerFEN: '8/5c2/6gk1/5BK2/8',
        responseFEN: '6gk1/5c2/8/5BK2/8',
      },
      {
        triggerFEN: '6gk1/5c2/6BK1/8/8',
        responseFEN: '4gk3/5c2/6BK1/8/8',
      },
      {
        triggerFEN: '8/5BK2/6gk1/5c2/8',
        responseFEN: '8/5BK2/8/5c2/5gk2',
      },
      {
        // Player captures the dark Penguin with Bear → Bear now sits next to
        // the dark Crane. Crane can't capture Bear (predator mechanic), so the
        // AI moves the dark Goat king (C6 → D5) instead.
        triggerFEN: '8/4BKc2/6gk1/8/8',
        responseFEN: '8/4BKc2/8/5gk2/8',
      },
      {
        // Chains from the penguin-capture line above: after Goat fled C6 → D5,
        // player advanced Bear B4 → C5 (diagonal step toward the Goat). Bear
        // now sits at C5 adjacent to Goat at D5, Crane parked at B5. AI flees
        // the Goat one square diagonal-down-right (D5 → E4) to keep racing
        // toward row 0 while opening distance from the Bear.
        triggerFEN: '8/5c2/5BK2/5gk2/8',
        responseFEN: '8/5c2/5BK2/8/4gk3',
      },
      {
        // Continuation of the chase line above: player followed with Bear
        // C5 → D4 (diagonal step toward Goat at E4). Bear and Goat are again
        // adjacent. AI races the Goat straight down E4 → E3, one row closer
        // to row 0 — straight-down is now safer than diagonal since Bear
        // can't catch a Goat moving along the E-column from D4 in one step.
        triggerFEN: '8/5c2/8/4BK3/4gk3',
        responseFEN: '8/5c2/8/4BK3/3gk4',
      },
      {
        // Sibling branch to the Penguin-capture line above: player captured
        // the dark Crane instead (Bear C4 → B5), so Bear now sits one square
        // above the up-facing Penguin. Goat king is still at C6. AI Traverses
        // the Goat far away (C6 → A3) to flee the advancing Bear.
        triggerFEN: '8/4p↑BK2/6gk1/8/8',
        responseFEN: '3gk4/4p↑BK2/8/8/8',
      },
      {
        // Bear advances to C5, sitting one square below the Goat king at C6.
        // AI evacuates the Goat far to A3 (col 0, row 3) using Traverse to
        // avoid the capture threat. Penguin (facing up) and Crane unchanged.
        triggerFEN: '8/4p↑c2/5BKgk1/8/8',
        responseFEN: '3gk4/4p↑c2/5BK2/8/8',
      },
      {
        // Player pushed Bear from C5 → C6 (advancing toward row 7) without
        // capturing the Penguin. Goat is still parked at A3 from the previous
        // override. AI races the Goat one square down toward row 0 (A3 → A2).
        // Penguin keeps the up-facing arrow so this trigger matches the live
        // board (which carries the rotation from the earlier override).
        triggerFEN: '3gk4/4p↑c2/6BK1/8/8',
        responseFEN: '2gk5/4p↑c2/6BK1/8/8',
      },
      {
        // Follow-up: player captured the Penguin with Bear (now Bear at B4,
        // Crane at B5, Goat parked at A4 from the previous override). AI
        // continues racing the Goat one square down toward row 0.
        triggerFEN: '3gk4/4BKc2/8/8/8',
        responseFEN: '2gk5/4BKc2/8/8/8',
      },
      {
        triggerFEN: '8/4p↑c2/6gk1/5BK2/8',
        responseFEN: '3gk4/4p↑c2/8/5BK2/8',
      },
    ],
    3: [ // Bear tutorial 4 (index 3) - "Race defence"
      {
        triggerFEN: '8/4M3/8/4BK3/3W1ck2',
        responseFEN: '8/4M3/8/4BK1ck1/3W4'
      },
    ]
    // Add overrides for other Bear tutorials here
  },
  'Crane': {
    0: [ // Crane tutorial 1 (index 0) - "Racing"
      {
        triggerFEN: '8/6mk1/8/4CK3/7c',
        responseFEN: '8/8/6mk1/4CK3/7c'
      },
      {
        triggerFEN: '8/4CK1mk1/8/8/7c',
        responseFEN: '6mk1/4CK3/8/8/7c'
      },
      {
        triggerFEN: '8/8/2CK3ck1/8/8',
        responseFEN: '8/5ck2/2CK5/8/8'
      },
    ],
    1: [ // Crane tutorial 2 (index 1) - "Standard race"
      {
        triggerFEN: '8/8/3b3b/8/2CK5',
        responseFEN: '8/8/7b/3b4/2CK5'
      },
      {
        triggerFEN: '8/8/7b/1CK1b4/8',
        responseFEN: '8/8/2b4b/1CK6/8'
      },
      {
        triggerFEN: '8/8/2b4b/8/2CK5',
        responseFEN: '8/8/7b/3b4/2CK5'
      },
      {
        triggerFEN: '2CK5/8/3b3b/8/8',
        responseFEN: '2CK5/3b4/7b/8/8'
      },
      {
        triggerFEN: '8/1CK1b4/7b/8/8',
        responseFEN: '8/1CK6/2b4b/8/8'
      },
      {
        triggerFEN: '2CK5/8/2b4b/8/8',
        responseFEN: '2CK5/3b4/7b/8/8'
      },
      {
        triggerFEN: '8/3b4/CK6b/8/8',
        responseFEN: '8/8/CK2b3b/8/8'
      },
      {
        triggerFEN: '8/8/CK6b/3b4/8',
        responseFEN: '8/8/CK2b3b/8/8'
      },
      {
        triggerFEN: '8/8/1CK1b3b/8/8',
        responseFEN: '8/8/1CKb4b/8/8'
      },
      {
        triggerFEN: '8/8/2b4b/8/3CK4',
        responseFEN: '8/8/2b5/6b1/3CK4'
      },
      {
        triggerFEN: '3CK4/8/2b4b/8/8',
        responseFEN: '3CK4/6b1/2b5/8/8'
      },
      {
        triggerFEN: '4CK3/6b1/2b5/8/8',
        responseFEN: '4CK3/5b2/2b5/8/8'
      },
      {
        triggerFEN: '8/8/2b5/6b1/4CK3',
        responseFEN: '8/8/2b5/5b2/4CK3'
      },
      {
        triggerFEN: '8/8/2b5/4CK1b1/8',
        responseFEN: '8/8/2b3b1/4CK3/8'
      },
      {
        triggerFEN: '8/4CK1b1/2b5/8/8',
        responseFEN: '8/4CK3/2b3b1/8/8'
      },
      {
        triggerFEN: '8/2CK5/7b/2b5/8',
        responseFEN: '8/2CK5/3b3b1/8/8'
      },
      {
        triggerFEN: '3CK4/8/3b3b/8/8',
        responseFEN: '3CK4/4b3/7b/8/8'
      },
      {
        triggerFEN: '8/2b5/6b/2CK5/8',
        responseFEN: '8/8/3b3b/2CK5/8'
      },
      {
        triggerFEN: '8/2b5/7b/2CK5/8',
        responseFEN: '8/8/3b3b/2CK5/8'
      },
      {
        triggerFEN: '8/8/3b3b/8/3CK4',
        responseFEN: '8/8/7b/4b3/3CK4'
      },
      {
        triggerFEN: '8/8/7b/2CK1b3/8',
        responseFEN: '8/8/3b3b/2CK5/8'
      },
      {
        triggerFEN: '8/2CK1b3/7b/8/8',
        responseFEN: '8/2CK5/3b3b/8/8'
      },
      {
        triggerFEN: '8/1CK6/3b3b/8/8',
        responseFEN: '8/1CK6/3b2b1/8/8'
      },
      {
        triggerFEN: '8/1CK1b4/6b1/8/8',
        responseFEN: '8/1CK1b4/5b2/8/8'
      },
      {
        triggerFEN: '2CK5/8/3b2b1/8/8',
        responseFEN: '2CK5/5b2/3b4/8/8'
      },
      {
        triggerFEN: '8/8/3b3b/1CK6/8',
        responseFEN: '8/8/3b2b1/1CK6/8'
      },
      {
        triggerFEN: '8/8/3b2b1/8/2CK5',
        responseFEN: '8/8/3b4/5b2/2CK5'
      },
      {
        triggerFEN: '8/1CK3b2/3b4/8/8',
        responseFEN: '8/1CK3b2/8/3b4/8'
      },
      {
        triggerFEN: '8/3CK1b2/8/3b4/8',
        responseFEN: '8/3CK4/4b3/3b4/8'
      },
      {
        triggerFEN: '4CK3/8/4b3/3b4/8',
        responseFEN: '4CK3/5b2/8/3b4/8'
      },
      {
        triggerFEN: '8/8/3b4/1CK3b2/8',
        responseFEN: '8/3b4/8/1CK3b2/8'
      },
      {
        triggerFEN: '8/3b4/8/3CK1b2/8',
        responseFEN: '8/3b4/4b3/3CK4/8'
      },
      {
        triggerFEN: '8/3b4/4b3/8/4CK3',
        responseFEN: '8/3b4/8/5b2/4CK3'
      },
      {
        triggerFEN: '8/3b4/8/2CK2b2/8',
        responseFEN: '8/3b4/8/2CK1b3/8'
      }
    ]
    // Add overrides for other Crane tutorials here
  },
  'Tree': {
    0: [ // Tree tutorial 1 (index 0)
      {
        triggerFEN: '6TK1/8/6bk1/8/8',
        responseFEN: '6TK1/7bk/8/8/8',
      },
      {
        triggerFEN: '4b1bk1/8/8/8/3TK4',
        responseFEN: '6bk1/5b2/8/8/3TK4',
      },
      {
        triggerFEN: '6bk1/5b2/8/8/4TK3',
        responseFEN: '6bk1/8/6b1/8/4TK3',
      },
      {
        triggerFEN: '5TK2/8/8/6bk1/8',
        responseFEN: '5TK2/8/5bk2/8/8',
      },
      {
        triggerFEN: '6TK1/8/5bk2/8/8',
        responseFEN: '6TK1/6bk1/8/8/8',
      },
      {
        triggerFEN: '4c1bk1/8/6BB/8/3TK4',
        responseFEN: '4c3/5bk2/6BB/8/3TK4',
      },
      {
        triggerFEN: '4c3/5bk2/6BB/8/4TK3',
        responseFEN: '8/3c1bk2/6BB/8/4TK3',
      },
      {
        triggerFEN: '4TK1bk1/8/6BB/8/8',
        responseFEN: '4TK3/5bk2/6BB/8/8',
      },
      {
        triggerFEN: '8/5bk2/6BB/8/4TK3',
        responseFEN: '8/8/5bkBB/8/4TK3',
      },
      {
        triggerFEN: '8/8/5bkBB/8/5TK2',
        responseFEN: '8/8/6BB/6bk1/5TK2',
      },
      {
        triggerFEN: '5TK2/8/6BB/6bk1/8',
        responseFEN: '5TK2/8/5bkBB/8/8',
      },
      {
        triggerFEN: '8/5TK2/6BB/6bk1/8',
        responseFEN: '8/5TK2/5bkBB/8/8',
      },
      {
        triggerFEN: '6TK1/8/5bkBB/8/8',
        responseFEN: '6TK1/6bk1/6BB/8/8',
      },
      {
        triggerFEN: '4c1bk1/3TK4/6BB/8/8',
        responseFEN: '4c3/3TK1bk2/6BB/8/8',
      },
      {
        triggerFEN: '4c1bk1/8/3TK2BB/8/8',
        responseFEN: '4c3/5bk2/3TK2BB/8/8',
      },
      {
        triggerFEN: '4c1bk1/8/6BB/3TK4/8',
        responseFEN: '4c3/5bk2/6BB/3TK4/8',
      },
      {
        triggerFEN: '4c3/5bk2/6BB/4TK3/8',
        responseFEN: '4c3/8/5bkBB/4TK3/8',
      },
      {
        triggerFEN: '3c4/8/5bkBB/8/5TK2',
        responseFEN: '3c4/8/6BB/6bk1/5TK2',
      },
      {
        triggerFEN: '8/3c1bk2/6BB/8/5TK2',
        responseFEN: '8/3c4/4bk1BB/8/5TK2',
      }
    ],
    1: [ // Tree tutorial 2 (index 1) - "Prevent Tree+Crane Victory"
      {
        triggerFEN: '3w4/8/3T+CK2b1/4w1w1/8',
        responseFEN: '3w4/8/3T+CK1b2/4w1w1/8',
      },
      {
        triggerFEN: '8/8/4b3/4w1w1/4T+CK3',
        responseFEN: '8/8/8/4wbw1/4T+CK3',
      },
      {
        triggerFEN: '4CK3/8/2w4b/4w1w1/8',
        responseFEN: '4CK3/6b1/2w5/4w1w1/8',
      }
    ]
  },
  'Monkey': {
    1: [ // Monkey tutorial 2 (index 1) - "Swing ability"
      {
        triggerFEN: '8/7ck/3MKT2r*/8/6t1',
        responseFEN: '8/7ck/3MKTr*2/8/6t1',
      }
    ],
    2: [ // Monkey tutorial 3 (index 2) - "Gamble" tutorial
      {
        triggerFEN: '8/8/4MK1bk1/8/8',
        responseFEN: '8/8/4MKbk2/8/8',
      }
    ]
  },
  'Wolf': {
    1: [ // Wolf tutorial 2 (index 1) - "Going it alone"
      {
        triggerFEN: '3b4/8/6bk1/4WK3/1b6',
        responseFEN: '3b4/8/5bk2/4WK3/1b6',
      },
      {
        triggerFEN: 'WK2b4/8/6bk1/8/2T5',
        responseFEN: 'WK2b4/8/5bk2/8/2T5',
      },
      {
        triggerFEN: '3b4/8/1WK3bk2/8/2T5',
        responseFEN: '3b4/8/1WK6/4bk3/2T5',
      },
      {
        triggerFEN: '3b4/8/8/4bk3/WK1T5',
        responseFEN: '3b4/8/8/3bk4/WK1T5',
      },
      {
        triggerFEN: 'WK2b4/8/5bk2/8/3T4',
        responseFEN: 'WK7/4b3/5bk2/8/3T4',
      },
      {
        triggerFEN: '8/2WK1b3/5bk2/8/3T4',
        responseFEN: '8/2WK1b3/8/4bk3/3T4',
      },
      {
        triggerFEN: 'WK7/4b3/5bk2/8/4T3',
        responseFEN: 'WK7/3b4/5bk2/8/4T3',
      },
      {
        triggerFEN: 'WK7/3b4/5bk2/8/5T2',
        responseFEN: 'WK7/3b4/8/4bk3/5T2',

      },
      {
        triggerFEN: 'WK7/3b4/8/4bk3/6T1',
        responseFEN: 'WK7/8/2b5/4bk3/6T1',

      }
    ],
    2: [ // Wolf tutorial 3 (index 2) - "Wolf Challenge"
      {
        triggerFEN: '5ct1/2FWK4/5b1wk/8/1m6',
        responseFEN: '5ct1/2FWK4/5b2/8/1m4wk1',
      }
    ]
  },
  'Rhino': {
    0: [ // Rhino tutorial 1 (index 0) - "Rhino movement"
      {
        triggerFEN: 'T7/5wk2/8/3R*K4/4w3',
        responseFEN: 'T7/8/3wk4/3R*K4/4w3',
        description: 'Move wolf pieces away from rhino, maintain hasMoved = true',
        postProcess: (gameState) => {
          // Find the Rhino piece and set hasMoved = true (since FEN resets it to false)
          for (let row = 0; row < gameState.board.length; row++) {
            for (let col = 0; col < gameState.board[row].length; col++) {
              const square = gameState.board[row][col];
              if (square.Pieces) {
                for (const piece of square.Pieces) {
                  if (piece.type === 'Rhino' && piece.isLight) {
                    piece.hasMoved = true;
                    return; // Found the rhino, exit early
                  }
                }
              }
            }
          }
        }
      }
    ]
  }
  // Add overrides for other constellations here
};

/**
 * Get all overrides for a specific tutorial
 * @param constellation The constellation name (e.g., 'Bear')
 * @param tutorialIndex The tutorial index (0-based)
 * @returns Array of overrides for this tutorial, or empty array if none exist
 */
export function getTutorialOverrides(constellation: string, tutorialIndex: number): TutorialOverride[] {
  const constellationOverrides = TUTORIAL_OVERRIDES[constellation];
  if (!constellationOverrides) {
    return [];
  }
  
  const tutorialOverrides = constellationOverrides[tutorialIndex];
  if (!tutorialOverrides) {
    return [];
  }
  
  return tutorialOverrides;
}

/**
 * Check if the current board state matches any override trigger
 * @param constellation The constellation name
 * @param tutorialIndex The tutorial index (0-based)
 * @param currentFEN The current board state as FEN
 * @param onMatch Optional callback when match is found (for UI logging)
 * @returns The matching override, or null if no match found
 */
export function findMatchingOverride(
  constellation: string, 
  tutorialIndex: number, 
  currentFEN: string,
  onMatch?: (override: TutorialOverride, constellation: string, tutorialIndex: number) => void
): TutorialOverride | null {
  const overrides = getTutorialOverrides(constellation, tutorialIndex);
  const normalizedCurrentFEN = normalizeFEN(currentFEN);
  
  // Get UI logger for debug output
  const uiLogger = (onMatch as any)?.uiLogger;
  if (uiLogger) {
    uiLogger(`🔍 NORMALIZED CURRENT: "${normalizedCurrentFEN}"`, 'info');
  }
  

  for (let i = 0; i < overrides.length; i++) {
    const override = overrides[i];
    const normalizedTriggerFEN = normalizeFEN(override.triggerFEN);

    if (normalizedTriggerFEN !== normalizedCurrentFEN) {
      // Show first difference
      const maxLen = Math.max(normalizedCurrentFEN.length, normalizedTriggerFEN.length);
      for (let j = 0; j < maxLen; j++) {
        if (normalizedCurrentFEN[j] !== normalizedTriggerFEN[j]) {
          break;
        }
      }
    }

    if (uiLogger) {
      uiLogger(`🔍 NORMALIZED TRIGGER${i + 1}: "${normalizedTriggerFEN}"`, 'info');
      uiLogger(`🔍 MATCH${i + 1} RESULT: ${normalizedTriggerFEN === normalizedCurrentFEN}`, 'info');
      
      // Character-by-character comparison for debugging
      if (normalizedTriggerFEN !== normalizedCurrentFEN) {
        uiLogger(`🔍 LENGTH COMPARE: Current=${normalizedCurrentFEN.length}, Trigger=${normalizedTriggerFEN.length}`, 'info');
        const maxLen = Math.max(normalizedCurrentFEN.length, normalizedTriggerFEN.length);
        let diffFound = false;
        for (let j = 0; j < maxLen && !diffFound; j++) {
          const currentChar = normalizedCurrentFEN[j] || '∅';
          const triggerChar = normalizedTriggerFEN[j] || '∅';
          if (currentChar !== triggerChar) {
            uiLogger(`🔍 FIRST DIFF: Position ${j} - Current='${currentChar}' vs Trigger='${triggerChar}'`, 'info');
            diffFound = true;
          }
        }
      }
    }
    
    if (normalizedTriggerFEN === normalizedCurrentFEN) {
      if (onMatch) {
        onMatch(override, constellation, tutorialIndex);
      }
      return override;
    }
  }
  
  return null;
}

/**
 * Apply a tutorial override with the specified delay
 * @param override The override to apply
 * @param applyFunction Function to call to apply the new FEN state
 * @param onApply Optional callback when override is applied (for UI logging)
 * @returns Promise that resolves when the override is applied
 */
export function applyTutorialOverride(
  override: TutorialOverride,
  applyFunction: (responseFEN: string) => void,
  onApply?: (override: TutorialOverride) => void,
  gameStateGetter?: () => any
): Promise<void> {
  const delay = override.delayMs || 200; // Default 200ms delay
  
  return new Promise<void>((resolve) => {
    setTimeout(() => {
      applyFunction(override.responseFEN);
      
      // Apply post-processing if defined
      if (override.postProcess && gameStateGetter) {
        const gameState = gameStateGetter();
        override.postProcess(gameState);
      }
      
      if (onApply) {
        onApply(override);
      }
      resolve();
    }, delay);
  });
}

/**
 * Check if we're currently in a tutorial that has overrides
 * @param constellation The constellation name
 * @param tutorialIndex The tutorial index (0-based)
 * @returns True if this tutorial has any overrides defined
 */
export function hasTutorialOverrides(constellation: string, tutorialIndex: number): boolean {
  return getTutorialOverrides(constellation, tutorialIndex).length > 0;
}